import React, { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Sparkles, Search, ChevronLeft, ChevronRight, Globe } from 'lucide-react';
import Layout from '../components/Layout';
import { seoKeywords, detectLang } from '../mock/seoKeywords';
import { LANGS, LANG_META, isLang, translateKw } from '../mock/seoLang';
import useSEO from '../hooks/useSEO';
import { useApp } from '../context/AppContext';
import NotFound from './NotFound';

const PER_PAGE = 300;

const TransfersIndex = () => {
  const { lang: langParam } = useParams();
  const navigate = useNavigate();
  const { kwVersion } = useApp();
  const badLang = !!langParam && !isLang(langParam);
  const lang = isLang(langParam) ? langParam : '';
  const total = seoKeywords.length * LANGS.length;
  useSEO(`Zürich Airport Transfers – ${seoKeywords.length.toLocaleString()} Strecken × ${LANGS.length} Sprachen | CAB.TAXI`, `${total.toLocaleString()} Zurich airport taxi & transfer pages in ${LANGS.length} languages.`, { skip: badLang, lang: lang || 'de', dir: lang ? LANG_META[lang].dir : 'ltr', alternates: [...LANGS.map((l) => ({ lang: l, href: `${window.location.origin}/${l}/transfers` })), { lang: 'x-default', href: `${window.location.origin}/transfers` }] });
  const [q, setQ] = useState('');
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [...seoKeywords];
    return seoKeywords.filter((k) => k.kw.toLowerCase().includes(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, kwVersion]);

  const pages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const current = Math.min(page, pages - 1);
  const slice = filtered.slice(current * PER_PAGE, current * PER_PAGE + PER_PAGE);
  const prefix = lang ? `/${lang}` : '';

  if (badLang) return <NotFound />;
  return (
    <Layout>
      <section className="bg-[color:var(--brand-dark)] text-white py-14">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 text-xs font-semibold uppercase mb-4"><Sparkles className="w-3.5 h-3.5" style={{ color: 'var(--brand-accent)' }} /> CAB.TAXI</div>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight">Zürich Airport Transfers</h1>
          <p className="mt-3 text-white/70 max-w-2xl" data-testid="transfers-total">{seoKeywords.length.toLocaleString()} Strecken × {LANGS.length} Sprachen = {total.toLocaleString()} Seiten · Festpreis</p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3 max-w-3xl">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/50" />
              <input
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(0); }}
                placeholder="Strecke oder Service suchen … (z.B. lugano, davos, hotel)"
                className="w-full rounded-full bg-white/10 border border-white/15 pl-11 pr-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-white/40"
                data-testid="transfers-search"
              />
            </div>
            <div className="relative">
              <Globe className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-white/50 pointer-events-none" />
              <select
                value={lang}
                onChange={(e) => navigate(e.target.value ? `/${e.target.value}/transfers` : '/transfers')}
                className="appearance-none rounded-full bg-white/10 border border-white/15 pl-11 pr-8 py-3 text-sm text-white focus:outline-none focus:border-white/40 cursor-pointer"
                data-testid="transfers-lang"
              >
                <option value="" className="text-black">Original</option>
                {LANGS.map((l) => <option key={l} value={l} className="text-black">{LANG_META[l].name}</option>)}
              </select>
            </div>
          </div>
        </div>
      </section>
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <p className="text-sm text-gray-500 mb-5" data-testid="transfers-count">{filtered.length.toLocaleString()} Ergebnisse · Seite {current + 1} / {pages}</p>
          <div className="flex flex-wrap gap-2" data-testid="transfers-index" dir={lang && LANG_META[lang].dir === 'rtl' ? 'rtl' : 'ltr'}>
            {slice.map((k) => (
              <Link key={k.slug} to={lang && lang !== detectLang(k.kw) ? `${prefix}/t/${k.slug}` : `/t/${k.slug}`} className="text-sm font-medium text-[color:var(--brand-dark)] bg-[color:var(--brand-muted)] rounded-full px-4 py-2 hover:bg-gray-200" data-testid={`transfers-link-${k.slug}`}>{lang ? translateKw(k.kw, lang) : k.kw}</Link>
            ))}
          </div>
          {pages > 1 && (
            <div className="flex items-center justify-center gap-4 mt-10" data-testid="transfers-pagination">
              <button onClick={() => setPage(Math.max(0, current - 1))} disabled={current === 0} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold disabled:opacity-40 hover:bg-gray-50" data-testid="transfers-prev"><ChevronLeft className="w-4 h-4" /> Zurück</button>
              <span className="text-sm text-gray-500">{current + 1} / {pages}</span>
              <button onClick={() => setPage(Math.min(pages - 1, current + 1))} disabled={current >= pages - 1} className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold disabled:opacity-40 hover:bg-gray-50" data-testid="transfers-next">Weiter <ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default TransfersIndex;
